/**
 * options.js --- Custom option components for rich text embedding
 *
 * This file contains all the custom embedded rich-text
 * fields that might be used in a Contentful post. Import
 * functions here in your file and specify them in the
 * "options" argument of documentToReactComponent.
 */

import React from "react"

import GlobalStyle from "../globals.module.scss"

/**
 * ImageLink: An embedded image that links to a URL
 */
export const ImageLink = ({ fields }) => {
  return (
    <>
      <br />
      <center>
        <a
          href={fields.url["en-US"]}
          target={"_blank"}
          rel="noopener noreferrer"
        >
          <img
            src={"http:" + fields.image["en-US"].fields.file["en-US"].url}
            alt={fields.title.enUS}
            style={{ width: fields.imageSize["en-US"] + "%", height: "auto" }}
          />
        </a>
      </center>
      <br />
    </>
  )
}

/**
 * ColoredLink: A link that's the color of the article's theme
 */
export const ColoredLink = ({ url, linkText, color }) => {
  return (
    <>
      <a
        href={`${url}`}
        style={{ color: color }}
        target={"_blank"}
        rel={"noopener noreferrer"}
      >
        <u>{linkText}</u>
      </a>
    </>
  )
}

/**
 * EmbeddedVimeo: An embedded Vimeo video with a caption
 */
export const EmbeddedVimeo = props => {
  const videoKey = props.videoUrl.substr(18)
  const videoColor = props.videoColor.substr(1)
  return (
    <center>
      <hr className={GlobalStyle.vimeoHR} />
      <div className={GlobalStyle.embeddedVimeo}>
        <div className={GlobalStyle.vimeoContainer}>
          <iframe
            src={`https://player.vimeo.com/video/${videoKey}?color=${videoColor}&title=0&byline=0&portrait=0`}
            className={GlobalStyle.embeddedVimeoiFrame}
            title={props.videoTitle}
            frameBorder={0}
            allow={"encrypted-media"}
          ></iframe>
        </div>
        <br />
        <i style={{ color: "#" + videoColor }}>{props.videoDescription}</i>
      </div>
      <hr className={GlobalStyle.vimeoHR} />
    </center>
  )
}

/**
 * EmbedddedYouTube: An embedded YouTube video with a caption
 */
export const EmbeddedYouTube = props => {
  const videoColor = props.videoColor.substr(1)
  return (
    <center>
      <hr className={GlobalStyle.vimeoHR} />
      <div className={GlobalStyle.videoContainer}>
        <iframe
          src={props.videoUrl}
          title={props.videoTitle}
          className={GlobalStyle.iframeYouTube}
          frameBorder={0}
        ></iframe>
      </div>
      <br />
      <i style={{ color: "#" + videoColor, fontFamily: "Corbel" }}>
        {props.videoDescription}
      </i>
      <hr className={GlobalStyle.vimeoHR} />
    </center>
  )
}

/**
 * EmbeddedBandcamp: An embedded Bandcamp playlist with a caption
 */
export const EmbeddedBandcamp = props => {
  const hashColor = props.bandcampColor
  const bandcampColor = props.bandcampColor.substr(1)
  const bandcampUrl = props.bandcampUrl + "/linkcol=" + bandcampColor
  return (
    <>
      <br />
      <hr />
      <div
        style={{
          left: "0vw",
          width: "100%",
          height: "120px",
          position: "relative",
        }}
      >
        <iframe
          src={bandcampUrl}
          className={GlobalStyle.embeddedBandCamp}
          title={props.bandcampTitle}
          allow={"encrypted-media"}
        ></iframe>
      </div>
      <div className={GlobalStyle.bandCampCaption} style={{ color: hashColor }}>
        <i>{props.bandcampCaption}</i>
      </div>
      <hr />
      <br />
    </>
  )
}

/**
 * EmbeddedHTML: An embedded HTML player
 */
 export const EmbeddedHTML = props => {
    return (
      <>
        <br />
        <hr />
        <div
          style={{
            left: "0vw",
            width: "100%",
            height: "120px",
            position: "relative",
          }}
        >
        <br />
        <center>
            <audio controls>
            <source src={props.url} type="audio/wav" />
            <track />
            </audio>
        </center>
        </div>
        <div className={GlobalStyle.bandCampCaption} style={{ color: props.color }}>
          <i>{props.caption}</i>
        </div>
        <hr />
        <br />
      </>
    )
  }

/**
 * EmbeddedSoundCloud: An embedded SoundCloud track with a caption
 */
export const EmbeddedSoundCloud = props => {
  return (
    <>
      <br />
      <hr />
      <div
        style={{
          left: "0vw",
          width: "100%",
          height: "120px",
          position: "relative",
          paddingBottom: "5vw",
        }}
      >
        <iframe
          title={"Embedded SoundCloud"}
          width="100%"
          height="180"
          scrolling="no"
          frameBorder={0}
          src={
            props.soundcloudUrl + `&color=${props.soundcloudColor.substr(1)}`
          }
        ></iframe>
      </div>
      <div
        className={GlobalStyle.bandCampCaption}
        style={{ color: props.soundcloudColor }}
      >
        <i>{props.soundcloudCaption}</i>
      </div>
      <hr />
      <br />
    </>
  )
}

/**
 * EmbeddedImage: An embedded image that links to a URL
 */
export const EmbeddedImage = props => {
  if (props.imageCaption === null) {
    return (
      <>
        <br />
        <center>
          <div
            className={GlobalStyle.embeddedImageContainer}
            style={{ color: props.captionColor }}
          >
            <img
              src={"http:" + props.imageURL}
              alt={props.alt}
              className={GlobalStyle.embeddedImage}
              style={{ width: props.size["en-US"] + "%" }}
            />
          </div>
        </center>
      </>
    )
  } else {
    return (
      <>
        <br />
        <center>
          <div
            className={GlobalStyle.embeddedImageContainer}
            style={{ color: props.captionColor }}
          >
            <hr />
            <br />
            <img
              src={"http:" + props.imageURL}
              alt={props.alt}
              className={GlobalStyle.embeddedImage}
              style={{ width: props.size["en-US"] + "%" }}
            />
            <br />
            <i>{props.imageCaption}</i>
            <hr />
          </div>
        </center>
        <br />
      </>
    )
  }
}

/**
 * BorderedVideo: Hopefully will never have to use
 * these ever again...
 */
export const BorderedVideo = props => {
  const hashColor = props.color
  const color = props.color.substr(1)

  let videoSrc = props.videoId

  /**
   * Match specific stylings with video types
   *
   * If a video is FaceBook type, its full URL goes straight
   * to the iframe src property
   */
  if (props.type === "Vimeo") {
    videoSrc = `https://player.vimeo.com/video/${props.videoId}?color=${color}&title=0&byline=0&portrait=0`
  } else if (props.type === "YouTube") {
    videoSrc = `https://www.youtube.com/embed/${props.videoId}`
  }

  return (
    <center>
      <div
        className={GlobalStyle.borderedVideo}
        style={{ color: hashColor, borderColor: hashColor }}
      >
        <h2 style={{ marginLeft: "1%", marginRight: "1%" }}>{props.header}</h2>
        <div className={GlobalStyle.videoContainer}>
          <iframe
            src={videoSrc}
            title={props.header}
            className={GlobalStyle.iframeYouTube}
            frameBorder={0}
          ></iframe>
        </div>
      </div>
    </center>
  )
}

/**
 * BlockQuote: A blockquote centered in the middle
 * of the article content
 */
export const BlockQuote = props => {
  return (
    <div className={GlobalStyle.blockQuoteContainer}>
      <div className={GlobalStyle.blockQuote}>{props.children}</div>
    </div>
  )
}

/**
 * CorbelText: A section with Corbel font face
 */
export const CorbelText = props => {
  return <div className={GlobalStyle.corbelText} style={{ color: props.color }}>{props.text}</div>
}

/**
 * Centered Text: A section with Corbel centered text
 */
 export const CenteredText = props => {
    return (
        <center>
            <br />
            <div className={GlobalStyle.corbelText} style={{ color: props.color }}>{props.text}</div>
            <br />
        </center>
    )
  }

/**
 * Single spaced text: A section with single spaced text
 */
export const SingleSpaceText = props => {
  return <div className={GlobalStyle.singleSpaceText}>{props.text}</div>
}

/**
 * SubArticleTitle: A title heading for a sub article
 */
export const SubArticleTitle = props => {
  let extraLineBreak = props.author === null ? null : <br />
  let title = props.title === null ? null : props.title.toUpperCase()
  let extraHR = props.title === null ? null : "————"

  return (
    <center>
      <br />
      <div style={{ fontFamily: "Corbel", color: props.color }}>
        <h1 style={{ marginBottom: "1vw" }}>{title}</h1>
        {extraHR}
        <div style={{ fontSize: "1.4em" }}>
          <br />
          <b>
            {props.author}
            {extraLineBreak}
            {props.authorSubtitle}
          </b>
        </div>
      </div>
      {extraLineBreak}
    </center>
  )
}
